import { Component, OnInit } from '@angular/core';
import { TitleService } from '@app/shared/services/title.service';
import { MenuService } from '@app/shared/services/menu.service';
import { StationsService } from '@app/shared/services/stations.service';
import { SessionService } from '@app/shared/services/session.service';
import { ExcelService } from '@app/shared/services/excel.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/internal/operators';
import { AlertService } from '@app/shared/services/alert.service';

@Component({
    selector: 'app-station-list',
    templateUrl: './station-list.component.html',
    styleUrls: ['./station-list.component.scss']
})
export class StationListComponent implements OnInit {

    public stations: Array<any> = [];
    public maxPages: number = 1;
    public search: string = '';
    public searchChanged: Subject<string> = new Subject<string>();
    public currentPage: number = 1;
    public loadingStatus: boolean = true;
    public noResult: boolean = false;
    public excelLoading: boolean = false;
    private alertService: AlertService;

    constructor(
        private titleService: TitleService,
        private menu: MenuService,
        private stationService: StationsService,
        public session: SessionService,
        private excelService: ExcelService,
        private router: Router
    ) {
        this.searchChanged
            .pipe(debounceTime(1000), distinctUntilChanged())
            .subscribe(model => {
                this.search = model;
                this.currentPage = 1;
                sessionStorage.setItem('StationFilterSearch', model);
                this.updateStations();
            });
    }

    retrieveFilters() {
        this.search = ('StationFilterSearch' in sessionStorage) ? sessionStorage.getItem('StationFilterSearch') : this.search;
    }

    ngOnInit() {
        this.titleService.setTitle(`Liste des stations / VOP`);
        this.menu.setMenuState('ressource', 'station', '');
        this.retrieveFilters();
        if ('StationList' in sessionStorage) {
            this.stations = JSON.parse(sessionStorage.getItem('StationList'));
            this.maxPages = parseInt(sessionStorage.getItem('StationListMaxPages'), 10);
            this.currentPage = parseInt(sessionStorage.getItem('StationListCurrentPage'), 10);
            this.loadingStatus = false;
        } else {
            this.updateStations();
        }
    }

    onSearchChange(search): void {
        this.searchChanged.next(search);
    }

    goToVehicleList(): void {
        this.router.navigate(['ressource/vehicle/list']);
    }

    setStations(res) {
        this.stations = res;
        sessionStorage.setItem('StationList', JSON.stringify(this.stations));
        sessionStorage.setItem('StationListMaxPages', this.maxPages.toString());
        sessionStorage.setItem('StationListCurrentPage', this.currentPage.toString());
    }

    updateStations() {
        this.currentPage = 1;
        this.loadingStatus = true;
        this.stationService.updateStationCount(this.search).then(() => {
            this.maxPages = this.stationService.maxPages;
        });
        this.stationService.updateStations(this.currentPage, this.search).then((res) => {
            this.loadingStatus = false;
            if (this.stationService.stations.length === 0) {
                this.noResult = true;
            } else {
                this.setStations(res);
                this.noResult = false;
            }
        });
    }

    nextPage(): any {
        this.loadingStatus = true;
        this.stationService.updateStationCount(this.search).then(() => {
            this.maxPages = this.stationService.maxPages;
            if (this.currentPage < this.stationService.maxPages) {
                this.currentPage += 1;
                this.stationService.updateStations(this.currentPage, this.search).then((res) => {
                    this.loadingStatus = false;
                    if (this.stationService.stations.length === 0) {
                        this.noResult = true;
                    } else {
                        this.setStations(res);
                        this.noResult = false;
                    }
                });
            }
        });
    }

    goPage(page): any {
        this.loadingStatus = true;
        this.stationService.updateStationCount(this.search).then(() => {
            this.maxPages = this.stationService.maxPages;
            this.currentPage = page;
            this.stationService.updateStations(this.currentPage, this.search).then((res) => {
                this.loadingStatus = false;
                if (this.stationService.stations.length === 0) {
                    this.noResult = true;
                } else {
                    this.noResult = false;
                    this.setStations(res);
                }
            });
        });
    }

    prevPage(): any {
        this.loadingStatus = true;
        this.stationService.updateStationCount(this.search).then(() => {
            this.maxPages = this.stationService.maxPages;
            if (this.currentPage > 1) {
                this.currentPage -= 1;
                this.stationService.updateStations(this.currentPage, this.search).then((res) => {
                    this.loadingStatus = false;
                    if (this.stationService.stations.length === 0) {
                        this.noResult = true;
                    } else {
                        this.setStations(res);
                        this.noResult = false;
                    }
                });
            }
        });
    }

    exportDataAsXLSX(): any {
        let datas = [];
        let cols = this.excelService.initCols(["Nom de la station", "Adresse", "Ville", "Code postal",
            "Pays", "Latitude", "Longitude"]);

        this.excelLoading = true;
        this.stationService.updateStationCount().then(async () => {
            this.maxPages = Math.max(this.stationService.maxPages, 1);
            for (let i = 1; i <= this.maxPages; i++) {
                let results = await this.stationService.updateStations(i, '');

                if (results.length == 0) {
                    this.excelLoading = false;
                    return this.alertService.alert("Aucune donnée à exporter");
                }

                results.forEach((result) => {
                    let data = {
                        "Nom de la station": result.name,
                        "Adresse": result.address,
                        "Ville": result.city,
                        "Code postal": result.zipCode,
                        "Pays": result.country,
                        "Latitude": result.latitude,
                        "Longitude": result.longitude
                    };

                    Object.values(data).forEach((value, index) => {
                        if (typeof value === 'string') {
                            cols[index].wch = Math.max(value.length, cols[index].wch);
                        }
                    });
                    datas.push(data);
                });
            }
            cols = this.excelService.extendCols(cols);
            this.excelService.exportExcel(datas, "vop-stations", cols);
            this.excelLoading = false;
        });
    }

}
